// ============================================================================
// ProfilePage — route /app/account/profile  (namespace: "trainerProfile")
// ----------------------------------------------------------------------------
// The trainer's PUBLIC marketing profile editor (Wave 7), fully wired to the
// real account-profile API:
//   • GET   /api/account/profile            → useTrainerProfile()
//   • PATCH /api/account/profile            → useUpdateTrainerProfile()
//   • POST  /api/account/{avatar,certificates,achievements} (multipart)
//   • PATCH /api/account/assets/{id}, DELETE …, POST …/assets/reorder
//
// Mobile-first single column (390) widening to a two-up grid from lg. Every
// section is clearly marked PUBLIC so the trainer always knows what visitors
// see. Image uploads show an instant local preview, a size guard (≤8 MB to
// mirror the server), and a per-gallery reorder + caption editor. The handle
// field gives live slug validation + availability feedback (mapping the
// server's 409/422 back to inline hints), and a "Make my profile public"
// toggle gates the public page. When live, the shareable
// <host>/t/{handle} URL is shown with copy + share +
// "view public profile" affordances.
//
// These are the PUBLIC media class (trainer_asset) — resized JPEGs served
// UNencrypted; strictly separate from the encrypted client photo store. The
// page never touches client data.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Badge,
  Banner,
  Button,
  Card,
  CardHeader,
  PhoneField,
  TextField,
  isE164,
} from "../../components";
import { ApiError } from "../../api";
import type { TrainerAsset, TrainerProfile } from "../../api";
import {
  useDeleteAsset,
  useReorderAssets,
  useTrainerProfile,
  useUpdateAsset,
  useUpdateTrainerProfile,
  useUploadAchievement,
  useUploadAvatar,
  useUploadCertificate,
} from "../../hooks";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";
import { ShareButtons, useShareProfile } from "../../profile/ShareProfile";

/** Mirror the server's public-media cap (VF_PUBLIC_MAX_BYTES default 8 MB). */
const ASSET_MAX_BYTES = 8 * 1024 * 1024;

const EASE = [0.22, 0.61, 0.36, 1] as const;

export default function ProfilePage() {
  const { t } = useTranslation("trainerProfile");
  const reduce = useReducedMotion();
  const profile = useTrainerProfile();

  const enter = (i: number) => ({
    duration: 0.45,
    delay: reduce ? 0 : 0.05 * (i + 1),
    ease: EASE,
  });

  return (
    <div className="space-y-6">
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
      >
        <Link
          to="/app/account"
          className="text-sm font-bold text-body-soft transition-colors hover:text-coral"
        >
          ← {t("backToAccount")}
        </Link>
        <p className="mt-3 text-sm font-bold uppercase tracking-kicker text-coral">
          {t("kicker")}
        </p>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightest text-body sm:text-4xl">
          {t("title")}
        </h1>
        <p className="mt-1 max-w-prose text-body-soft">{t("subtitle")}</p>
      </motion.div>

      {profile.isError ? (
        <ErrorBlock onRetry={() => void profile.refetch()} />
      ) : profile.isLoading || !profile.data ? (
        <LoadingBlock />
      ) : (
        <Sections data={profile.data} reduce={!!reduce} enter={enter} />
      )}
    </div>
  );
}

function Sections({
  data,
  reduce,
  enter,
}: {
  data: TrainerProfile;
  reduce: boolean;
  enter: (i: number) => { duration: number; delay: number; ease: typeof EASE };
}) {
  const animate = (i: number, node: React.ReactNode) => (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={enter(i)}
    >
      {node}
    </motion.div>
  );

  return (
    <div className="space-y-6">
      {/* Share + visibility sit up top so the outcome is front-and-centre. */}
      {animate(0, <PublishCard data={data} reduce={reduce} />)}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        {animate(1, <AvatarCard data={data} reduce={reduce} />)}
        {animate(2, <HandleCard data={data} reduce={reduce} />)}
      </div>

      {animate(3, <IdentityCard data={data} reduce={reduce} />)}
      {animate(4, <PhoneCard data={data} reduce={reduce} />)}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        {animate(
          5,
          <GalleryCard
            ns="certificates"
            kind="certificate"
            items={data.assets.certificates}
            reduce={reduce}
          />,
        )}
        {animate(
          6,
          <GalleryCard
            ns="achievements"
            kind="achievement"
            items={data.assets.achievements}
            reduce={reduce}
          />,
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Animated success / error feedback line (collapses under reduced motion).
// ----------------------------------------------------------------------------
function FeedbackSlot({
  tone,
  message,
  reduce,
}: {
  tone: "success" | "danger";
  message: string | null;
  reduce: boolean;
}) {
  return (
    <AnimatePresence initial={false}>
      {message && (
        <motion.div
          key={message}
          initial={reduce ? false : { opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: "auto", marginTop: 12 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0, marginTop: 0 }}
          transition={{ duration: 0.28, ease: EASE }}
          style={{ overflow: "hidden" }}
        >
          <Banner tone={tone}>{message}</Banner>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** A small "PUBLIC" pill so every section makes clear what visitors see. */
function PublicTag() {
  const { t } = useTranslation("trainerProfile");
  return (
    <Badge tone="info" dot title={t("publicTagHint")}>
      {t("publicTag")}
    </Badge>
  );
}

// ----------------------------------------------------------------------------
// Publish / share — the visibility toggle + the live shareable link.
// ----------------------------------------------------------------------------
function PublishCard({ data, reduce }: { data: TrainerProfile; reduce: boolean }) {
  const { t } = useTranslation("trainerProfile");
  const update = useUpdateTrainerProfile();
  const [error, setError] = useState<string | null>(null);

  const hasHandle = !!data.handle;
  const isPublic = data.public_enabled;
  // The server only returns a non-null public_url when handle + enabled; build a
  // best-effort preview link as soon as a handle exists so the trainer can copy
  // it even while still hidden (it just 404s until they flip the toggle).
  const previewUrl = data.public_url;

  // Reuse the shared share logic (native Web Share API + copy fallback) so the
  // editor and the dashboard share card never drift apart.
  const share = useShareProfile(previewUrl, {
    title: t("share.shareTitle", { name: data.display_name || t("title") }),
    text: t("share.shareText"),
  });

  const toggle = () => {
    if (update.isPending) return;
    setError(null);
    update.mutate(
      { public_enabled: !isPublic },
      { onError: () => setError(t("publish.saveFailed")) },
    );
  };

  return (
    <Card tone={isPublic ? "ember" : "light"} className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3
            className={
              isPublic
                ? "font-display text-lg font-bold text-white"
                : "font-display text-lg font-bold text-body"
            }
          >
            {t("publish.title")}
          </h3>
          <p
            className={
              isPublic
                ? "mt-1 text-sm text-white/85"
                : "mt-1 text-sm text-body-muted"
            }
          >
            {t("publish.subtitle")}
          </p>
        </div>
        <Badge tone={isPublic ? "success" : "neutral"} dot>
          {isPublic ? t("publish.on") : t("publish.off")}
        </Badge>
      </div>

      {/* Toggle row */}
      <div
        className={
          isPublic
            ? "flex items-center justify-between gap-4 rounded-lg bg-white/10 px-4 py-3"
            : "flex items-center justify-between gap-4 rounded-lg bg-surface-sunken px-4 py-3"
        }
      >
        <div className="min-w-0">
          <p
            className={
              isPublic
                ? "font-display text-sm font-bold text-white"
                : "font-display text-sm font-bold text-body"
            }
          >
            {t("publish.toggleLabel")}
          </p>
          <p
            className={
              isPublic ? "text-sm text-white/80" : "text-sm text-body-muted"
            }
          >
            {hasHandle ? t("publish.toggleHint") : t("publish.needHandle")}
          </p>
        </div>
        <Toggle
          on={isPublic}
          disabled={(!hasHandle && !isPublic) || update.isPending}
          onToggle={toggle}
          label={t("publish.toggleLabel")}
        />
      </div>

      {/* Share block — only meaningful with a link. */}
      {previewUrl ? (
        <div className="space-y-3 rounded-lg bg-surface-sunken px-4 py-4">
          <p className="font-display text-xs font-bold uppercase tracking-kicker text-body-faint">
            {t("share.yourUrl")}
          </p>
          <div className="flex items-center gap-2 overflow-hidden rounded-md bg-surface px-3 py-2.5 ring-1 ring-line">
            <span className="truncate text-sm font-medium text-body" dir="ltr">
              {previewUrl}
            </span>
          </div>
          <ShareButtons url={previewUrl} controls={share} />
          {share.failed && (
            <p className="text-sm font-medium text-danger">
              {t("share.copyFailed")}
            </p>
          )}
        </div>
      ) : (
        <p
          className={
            isPublic
              ? "rounded-md bg-white/10 px-4 py-3 text-sm text-white/85"
              : "rounded-md bg-surface-sunken px-4 py-3 text-sm text-body-soft"
          }
        >
          {t("share.notLive")}
        </p>
      )}

      <FeedbackSlot tone="danger" message={error} reduce={reduce} />
    </Card>
  );
}

/** Minimal accessible on/off switch in the Pulse palette. */
function Toggle({
  on,
  disabled,
  onToggle,
  label,
}: {
  on: boolean;
  disabled?: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={
        "relative h-7 w-12 shrink-0 rounded-pill transition-colors duration-fast " +
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt focus-visible:ring-offset-2 " +
        "disabled:cursor-not-allowed disabled:opacity-50 " +
        (on ? "bg-lime" : "bg-line-strong")
      }
    >
      <span
        className={
          "absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-fast " +
          (on ? "translate-x-6" : "translate-x-1")
        }
      />
    </button>
  );
}

// ----------------------------------------------------------------------------
// Avatar — single public headshot. Upload replaces the old one server-side.
// ----------------------------------------------------------------------------
function AvatarCard({ data, reduce }: { data: TrainerProfile; reduce: boolean }) {
  const { t } = useTranslation("trainerProfile");
  const upload = useUploadAvatar();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const current = data.assets.avatar[0] ?? null;
  // The local preview wins while a fresh file uploads; otherwise the served URL.
  const shown = preview ?? current?.url ?? null;

  // Revoke the object URL when it changes / unmounts (avoid a memory leak).
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setSaved(false);
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    if (f.size > ASSET_MAX_BYTES) {
      setError(t("avatar.tooLarge"));
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const url = URL.createObjectURL(f);
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return url;
    });
    upload.mutate(
      { file: f },
      {
        onSuccess: () => setSaved(true),
        onError: (err) => {
          setError(
            err instanceof ApiError && err.status === 422
              ? t("avatar.invalid")
              : t("avatar.failed"),
          );
        },
      },
    );
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Card className="space-y-5">
      <CardHeader
        title={t("avatar.title")}
        subtitle={t("avatar.subtitle")}
        action={<PublicTag />}
      />

      <div className="flex items-center gap-5">
        {shown ? (
          <img
            src={shown}
            alt={t("avatar.alt")}
            className="h-24 w-24 shrink-0 rounded-full object-cover ring-2 ring-line"
          />
        ) : (
          <div className="grid h-24 w-24 shrink-0 place-items-center rounded-full bg-surface-sunken text-body-faint ring-2 ring-line">
            <svg viewBox="0 0 24 24" className="h-10 w-10" fill="none" aria-hidden>
              <circle cx="12" cy="8.5" r="3.5" stroke="currentColor" strokeWidth="1.8" />
              <path
                d="M4.5 20a7.5 7.5 0 0 1 15 0"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={onPick}
            className="hidden"
            id="vf-avatar-input"
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            className="w-full sm:w-auto"
          >
            {upload.isPending
              ? t("avatar.uploading")
              : current
                ? t("avatar.replace")
                : t("avatar.upload")}
          </Button>
          <p className="mt-2 text-sm text-body-muted">{t("avatar.hint")}</p>
        </div>
      </div>

      <FeedbackSlot
        tone="success"
        message={saved ? t("avatar.uploaded") : null}
        reduce={reduce}
      />
      <FeedbackSlot tone="danger" message={error} reduce={reduce} />
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Handle — the public URL slug, with live slug validation + availability.
// ----------------------------------------------------------------------------
const HANDLE_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function HandleCard({ data, reduce }: { data: TrainerProfile; reduce: boolean }) {
  const { t } = useTranslation("trainerProfile");
  const update = useUpdateTrainerProfile();

  const stored = data.handle ?? "";
  const [handle, setHandle] = useState(stored);
  const [serverErr, setServerErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setHandle(stored);
  }, [stored]);

  const trimmed = handle.trim().toLowerCase();
  const dirty = trimmed !== stored;

  // Client-side slug check mirrors db.validate_handle (charset + length).
  const localError = useMemo<string | null>(() => {
    if (trimmed === "") return null; // empty = leave unchanged
    if (trimmed.length < 3 || trimmed.length > 30) return t("handle.malformed");
    if (!HANDLE_RE.test(trimmed)) return t("handle.malformed");
    return null;
  }, [trimmed, t]);

  const showAvailable = dirty && trimmed !== "" && !localError && !serverErr;

  const onChange = (raw: string) => {
    // Soft-normalize to the slug charset as the trainer types.
    const next = raw.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setHandle(next);
    setServerErr(null);
    setSaved(false);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || localError || update.isPending || trimmed === "") return;
    setServerErr(null);
    setSaved(false);
    update.mutate(
      { handle: trimmed },
      {
        onSuccess: () => setSaved(true),
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            setServerErr(t("handle.taken"));
          } else if (err instanceof ApiError && err.status === 422) {
            const detail =
              typeof err.detail === "string" ? err.detail : "";
            setServerErr(
              detail.includes("reserved")
                ? t("handle.reserved")
                : t("handle.malformed"),
            );
          } else {
            setServerErr(t("identity.saveFailed"));
          }
        },
      },
    );
  };

  return (
    <Card className="space-y-5">
      <CardHeader
        title={t("handle.title")}
        subtitle={t("handle.subtitle")}
        action={<PublicTag />}
      />

      <form className="space-y-3" onSubmit={onSubmit} noValidate>
        <div>
          <label
            htmlFor="vf-handle"
            className="mb-1.5 block font-display text-sm font-bold text-body"
          >
            {t("handle.label")}
          </label>
          <div
            className={
              "flex items-stretch overflow-hidden rounded-md bg-surface ring-1 ring-inset shadow-xs transition-shadow " +
              ((localError || serverErr) && dirty
                ? "ring-danger"
                : "ring-line-strong focus-within:ring-2 focus-within:ring-volt")
            }
          >
            <span
              className="flex select-none items-center bg-surface-sunken px-3 text-sm text-body-muted"
              dir="ltr"
            >
              {t("handle.prefix", {
                host:
                  typeof window !== "undefined" ? window.location.host : "",
              })}
            </span>
            <input
              id="vf-handle"
              value={handle}
              placeholder={t("handle.placeholder")}
              dir="ltr"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="url"
              aria-invalid={!!(localError || serverErr) && dirty}
              onChange={(e) => onChange(e.target.value)}
              className="min-w-0 flex-1 bg-transparent px-3 py-3 text-base text-body placeholder:text-body-faint focus:outline-none"
            />
          </div>
          {(localError || serverErr) && dirty ? (
            <p className="mt-1.5 text-sm font-medium text-danger">
              {serverErr ?? localError}
            </p>
          ) : showAvailable ? (
            <p className="mt-1.5 text-sm font-medium text-lime">
              {t("handle.available")}
            </p>
          ) : (
            <p className="mt-1.5 text-sm text-body-muted">{t("handle.hint")}</p>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            className="w-full sm:w-auto"
            disabled={!dirty || !!localError || trimmed === "" || update.isPending}
          >
            {update.isPending ? t("handle.saving") : t("handle.save")}
          </Button>
        </div>
      </form>

      <FeedbackSlot
        tone="success"
        message={saved ? t("handle.saved") : null}
        reduce={reduce}
      />
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Identity — display name + bio + background (all public text fields).
// ----------------------------------------------------------------------------
function IdentityCard({ data, reduce }: { data: TrainerProfile; reduce: boolean }) {
  const { t } = useTranslation("trainerProfile");
  const update = useUpdateTrainerProfile();

  const [name, setName] = useState(data.display_name ?? "");
  const [bio, setBio] = useState(data.bio ?? "");
  const [background, setBackground] = useState(data.background ?? "");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setName(data.display_name ?? ""), [data.display_name]);
  useEffect(() => setBio(data.bio ?? ""), [data.bio]);
  useEffect(() => setBackground(data.background ?? ""), [data.background]);

  const patch = useMemo(() => {
    const next: { display_name?: string | null; bio?: string | null; background?: string | null } = {};
    const tn = name.trim();
    if (tn !== (data.display_name ?? "")) next.display_name = tn === "" ? null : tn;
    const tb = bio.trim();
    if (tb !== (data.bio ?? "")) next.bio = tb === "" ? null : tb;
    const tbg = background.trim();
    if (tbg !== (data.background ?? "")) next.background = tbg === "" ? null : tbg;
    return next;
  }, [name, bio, background, data.display_name, data.bio, data.background]);

  const dirty = Object.keys(patch).length > 0;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || update.isPending) return;
    setError(null);
    setSaved(false);
    update.mutate(patch, {
      onSuccess: () => setSaved(true),
      onError: () => setError(t("identity.saveFailed")),
    });
  };

  return (
    <Card className="space-y-5">
      <CardHeader
        title={t("identity.title")}
        subtitle={t("identity.subtitle")}
        action={<PublicTag />}
      />

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <TextField
          label={t("identity.displayName")}
          value={name}
          placeholder={t("identity.displayNamePlaceholder")}
          hint={t("identity.displayNameHint")}
          autoComplete="name"
          onChange={(e) => {
            setName(e.target.value);
            setSaved(false);
            setError(null);
          }}
        />

        <Textarea
          id="vf-bio"
          label={t("identity.bio")}
          value={bio}
          rows={2}
          placeholder={t("identity.bioPlaceholder")}
          hint={t("identity.bioHint")}
          onChange={(v) => {
            setBio(v);
            setSaved(false);
            setError(null);
          }}
        />

        <Textarea
          id="vf-background"
          label={t("identity.background")}
          value={background}
          rows={5}
          placeholder={t("identity.backgroundPlaceholder")}
          hint={t("identity.backgroundHint")}
          onChange={(v) => {
            setBackground(v);
            setSaved(false);
            setError(null);
          }}
        />

        <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
          <span className="order-2 text-sm text-body-faint sm:order-1">
            {dirty ? t("identity.noChanges") : ""}
          </span>
          <Button
            type="submit"
            className="order-1 w-full sm:order-2 sm:w-auto"
            disabled={!dirty || update.isPending}
          >
            {update.isPending ? t("identity.saving") : t("identity.save")}
          </Button>
        </div>
      </form>

      <FeedbackSlot
        tone="success"
        message={saved ? t("identity.saved") : null}
        reduce={reduce}
      />
      <FeedbackSlot tone="danger" message={error} reduce={reduce} />
    </Card>
  );
}

/** A labelled, Pulse-styled multiline text input (matches TextField visuals). */
function Textarea({
  id,
  label,
  value,
  rows,
  placeholder,
  hint,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  rows: number;
  placeholder?: string;
  hint?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="w-full">
      <label
        htmlFor={id}
        className="mb-1.5 block font-display text-sm font-bold text-body"
      >
        {label}
      </label>
      <textarea
        id={id}
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
      />
      {hint && <p className="mt-1.5 text-sm text-body-muted">{hint}</p>}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Phone — E.164 capture (NOT public on the page; for the trainer's records).
// ----------------------------------------------------------------------------
function PhoneCard({ data, reduce }: { data: TrainerProfile; reduce: boolean }) {
  const { t } = useTranslation("trainerProfile");
  const update = useUpdateTrainerProfile();

  const stored = data.phone ?? "";
  const [phone, setPhone] = useState(stored);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setPhone(stored), [stored]);

  const dirty = phone !== stored;
  const invalid = phone !== "" && !isE164(phone);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || update.isPending) return;
    if (invalid) {
      setError(t("phone.invalid"));
      return;
    }
    setError(null);
    setSaved(false);
    update.mutate(
      { phone: phone === "" ? null : phone },
      {
        onSuccess: () => setSaved(true),
        onError: () => setError(t("identity.saveFailed")),
      },
    );
  };

  return (
    <Card className="space-y-5">
      <CardHeader title={t("phone.title")} subtitle={t("phone.subtitle")} />

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <PhoneField
          label={t("phone.label")}
          countryLabel={t("phone.countryLabel")}
          value={phone}
          placeholder={t("phone.placeholder")}
          hint={t("phone.hint")}
          error={invalid && dirty ? t("phone.invalid") : undefined}
          onChange={(v) => {
            setPhone(v);
            setSaved(false);
            setError(null);
          }}
        />

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            className="w-full sm:w-auto"
            disabled={!dirty || update.isPending}
          >
            {update.isPending ? t("phone.saving") : t("phone.save")}
          </Button>
        </div>
      </form>

      <FeedbackSlot
        tone="success"
        message={saved ? t("phone.saved") : null}
        reduce={reduce}
      />
      <FeedbackSlot tone="danger" message={error} reduce={reduce} />
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Gallery — shared certificate / achievement editor (add + caption + reorder
// + delete). `ns` selects the i18n keys; `kind` drives the upload + reorder.
// ----------------------------------------------------------------------------
function GalleryCard({
  ns,
  kind,
  items,
  reduce,
}: {
  ns: "certificates" | "achievements";
  kind: "certificate" | "achievement";
  items: TrainerAsset[];
  reduce: boolean;
}) {
  const { t } = useTranslation("trainerProfile");
  const uploadCert = useUploadCertificate();
  const uploadAch = useUploadAchievement();
  const reorder = useReorderAssets();
  const upload = kind === "certificate" ? uploadCert : uploadAch;

  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // Order by `ord` then id so the list reflects the saved sequence.
  const ordered = useMemo(
    () =>
      [...items].sort((a, b) => {
        const ao = a.ord ?? 0;
        const bo = b.ord ?? 0;
        if (ao !== bo) return ao - bo;
        return a.id - b.id;
      }),
    [items],
  );

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    if (f.size > ASSET_MAX_BYTES) {
      setError(t("gallery.tooLarge"));
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    upload.mutate(
      { file: f },
      {
        onError: (err) =>
          setError(
            err instanceof ApiError && err.status === 422
              ? t("gallery.invalid")
              : t("gallery.failed"),
          ),
      },
    );
    if (fileRef.current) fileRef.current.value = "";
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= ordered.length) return;
    const ids = ordered.map((a) => a.id);
    [ids[index], ids[next]] = [ids[next], ids[index]];
    reorder.mutate({ kind, orderedIds: ids });
  };

  return (
    <Card className="space-y-5">
      <CardHeader
        title={t(`${ns}.title`)}
        subtitle={t(`${ns}.subtitle`)}
        action={<PublicTag />}
      />

      <div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png"
          onChange={onPick}
          className="hidden"
          id={`vf-${kind}-input`}
        />
        <Button
          size="sm"
          variant="secondary"
          onClick={() => fileRef.current?.click()}
          disabled={upload.isPending}
          className="w-full sm:w-auto"
        >
          {upload.isPending ? t(`${ns}.adding`) : t(`${ns}.add`)}
        </Button>
      </div>

      <FeedbackSlot tone="danger" message={error} reduce={reduce} />

      {ordered.length === 0 ? (
        <p className="rounded-md bg-surface-sunken px-4 py-6 text-center text-sm text-body-soft">
          {t(`${ns}.empty`)}
        </p>
      ) : (
        <ul className="space-y-3">
          {ordered.map((asset, i) => (
            <GalleryRow
              key={asset.id}
              ns={ns}
              asset={asset}
              isFirst={i === 0}
              isLast={i === ordered.length - 1}
              onMove={(dir) => move(i, dir)}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function GalleryRow({
  ns,
  asset,
  isFirst,
  isLast,
  onMove,
}: {
  ns: "certificates" | "achievements";
  asset: TrainerAsset;
  isFirst: boolean;
  isLast: boolean;
  onMove: (dir: -1 | 1) => void;
}) {
  const { t } = useTranslation("trainerProfile");
  const updateAsset = useUpdateAsset();
  const del = useDeleteAsset();

  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(asset.caption ?? "");

  useEffect(() => setCaption(asset.caption ?? ""), [asset.caption]);

  const saveCaption = () => {
    updateAsset.mutate(
      { id: asset.id, body: { caption: caption.trim() || null } },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <li className="rounded-lg bg-surface-sunken p-3">
      <div className="flex gap-3">
        <img
          src={asset.url}
          alt={asset.caption ?? t(`${ns}.title`)}
          loading="lazy"
          className="h-20 w-20 shrink-0 rounded-md object-cover ring-1 ring-line"
        />

        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="space-y-2">
              <TextField
                label={t(`${ns}.captionLabel`)}
                hideLabel
                value={caption}
                placeholder={t(`${ns}.captionPlaceholder`)}
                onChange={(e) => setCaption(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={saveCaption}
                  disabled={updateAsset.isPending}
                >
                  {t("gallery.saveCaption")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setCaption(asset.caption ?? "");
                    setEditing(false);
                  }}
                >
                  {t("gallery.cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-body">
              {asset.caption || (
                <span className="text-body-faint">
                  {t(`${ns}.captionPlaceholder`)}
                </span>
              )}
            </p>
          )}

          {!editing && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <IconBtn
                label={t("gallery.moveUp")}
                disabled={isFirst}
                onClick={() => onMove(-1)}
              >
                <path d="M12 5v14M6 11l6-6 6 6" />
              </IconBtn>
              <IconBtn
                label={t("gallery.moveDown")}
                disabled={isLast}
                onClick={() => onMove(1)}
              >
                <path d="M12 19V5M6 13l6 6 6-6" />
              </IconBtn>
              <IconBtn label={t("gallery.edit")} onClick={() => setEditing(true)}>
                <path d="M4 20h4L18.5 9.5a2 2 0 0 0-2.8-2.8L5 17.5z" />
              </IconBtn>
              <IconBtn
                label={t("gallery.delete")}
                tone="danger"
                disabled={del.isPending}
                onClick={() => {
                  if (window.confirm(t("gallery.deleteConfirm")))
                    del.mutate(asset.id);
                }}
              >
                <path d="M5 7h14M9 7V5h6v2M7 7l1 12h8l1-12" />
              </IconBtn>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

/** A small square icon button used for the gallery row actions. */
function IconBtn({
  label,
  onClick,
  disabled,
  tone = "default",
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "danger";
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={
        "grid h-9 w-9 place-items-center rounded-md bg-surface text-body-soft ring-1 ring-line transition-colors " +
        "disabled:cursor-not-allowed disabled:opacity-40 " +
        (tone === "danger"
          ? "hover:bg-danger/10 hover:text-danger"
          : "hover:bg-surface-sunken hover:text-body")
      }
    >
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {children}
      </svg>
    </button>
  );
}
